/**
 * The two section cases that must be COMPONENTS rather than functions.
 *
 * Both decide what to render from a query, and a query has to run inside a
 * component — the dispatcher is a plain function. Split out of
 * `DropSectionRenderer` for the 400-line ceiling.
 */
import React from "react";
import {MissionsSection} from "@/widgets/MissionsSection";
import {MissionsAsEngagementStrip} from "@/widgets/MissionsAsEngagementStrip";
import {GameNewsSection} from "@/widgets/GameNewsSection";
import {useGetChallenge} from "@/hooks/challenges/useGetChallenge";
import {useGetDropGameNews} from "@/hooks/drop/useGetDropGameNews";

export const MissionsCase: React.FC<{
    challengeId?: string;
    hubBrandName?: string;
    hubBrandColor?: string;
}> = ({challengeId, hubBrandName, hubBrandColor}) => {
    /*
     * Render EXACTLY ONE path — never both (that was the duplicate-content bug).
     * We read the same challenge the strip reads (React Query dedupes the call)
     * so we can decide from here: when the challenge has active quest configs
     * the strip owns the render; otherwise the legacy MissionsSection does.
     * While the challenge is loading we defer the decision (the strip itself
     * renders null during load, so this matches its behavior and avoids a
     * flash of the legacy section that then swaps to the strip).
     */
    const {data: challenge, isLoading} = useGetChallenge(challengeId);

    if (isLoading) {
        return null;
    }

    const cast = challenge as unknown as { questConfigs?: { isActive?: boolean }[] } | undefined;
    const stripHasContent = (cast?.questConfigs ?? []).some(q => q.isActive);

    return stripHasContent
        ? <MissionsAsEngagementStrip
            challengeId={challengeId}
            hubBrandName={hubBrandName}
        />
        : <MissionsSection
            challengeId={challengeId}
            hubBrandName={hubBrandName}
            hubBrandColor={hubBrandColor}
        />;
};

/**
 * The title's Steam announcements.
 *
 * Its own component because the fetch has to happen inside a component, and
 * because the decision it makes is "render nothing" more often than not: a
 * title with no Steam page, or a page with no posts yet, returns null and takes
 * the whole section with it, heading included — which is the point.
 *
 * Steam being UNREACHABLE is the exception and renders a visible error instead.
 * A configured news rail vanishing silently on an outage is indistinguishable
 * from one that was never configured, so the two cases must not collapse.
 */
/*
 * No `steamAppId` prop. The feed is fetched by slug and the backend resolves
 * which Steam app to ask (the Drop's own, or the linked game's). The id was
 * passed in only to build the "All news →" link out to the storefront, and
 * that link is gone.
 */
export const GameNewsCase: React.FC<{ slug?: string }> = ({slug}) => {
    const {data, isError} = useGetDropGameNews(slug);

    /*
     * A failed fetch is NOT an empty feed. Collapsing the two would hide a
     * configured news rail whenever Steam is unreachable, with nothing on
     * screen to say why — the same failure CreatorTeamSection guards against
     * for the roster. Say it instead.
     */
    if (isError) {
        return (
            <section className="game-news" aria-label="Game news">
                <p className="game-news__error">
                    Couldn’t load game news just now. Refresh to try again.
                </p>
            </section>
        );
    }

    const items = data?.items ?? [];
    if (!items.length) return null;

    return <GameNewsSection items={items} />;
};

